import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, zip } from 'rxjs';
import {
  AddCompanyRequestModel,
  AddCompanyResponseModel,
  CompanyLookupResponseModel,
} from '../models/company.model';
import { ContactsApiService } from '../services/api/contacts-api.service';
import {
  AddContactRequestModel,
  AddContactResponseModel,
} from '../models/contacts.model';
import { CompanyApiService } from '../services/api/company-api.service';
import {
  AddApplicationRequestModel,
  AddApplicationResponseModel,
  GetApplicationResponseModel,
} from '../models/applications.model';
import { ApplicationsApiService } from '../services/api/applications-api.service';
import { CanActivate, Router, ActivatedRoute } from '@angular/router';
import { switchMap, tap } from 'rxjs/operators';
import { PersonalInfoService } from './personal-info.service';
import { ContactsService } from '../services/contacts.service';
import { TrackingService } from '../services/tracking.service';
import { TicketType } from '../app.consts';
import { GetLeadResponseModel } from '../models/lead.model';
import { formatPhoneNumberBackToClient } from '../utils/format-phone';

@Injectable()
export class BasicInfoService implements CanActivate {
  private _amount = new BehaviorSubject(0);
  private _duration = new BehaviorSubject(0);
  private _email = new BehaviorSubject('');
  private _phoneNumber = new BehaviorSubject('');
  private _companyNameOrId = new BehaviorSubject('');
  private _reason = new BehaviorSubject('');
  private _personNumber = new BehaviorSubject('');
  private _companyAssociation = new BehaviorSubject('');
  private _company = new BehaviorSubject<CompanyLookupResponseModel>(
    {} as CompanyLookupResponseModel
  );
  private _leadId = new BehaviorSubject('');
  private _ticketType = new BehaviorSubject(null);
  private _agreedToTerms = new BehaviorSubject(false);

  constructor(
    private router: Router,
    private contactsApi: ContactsApiService,
    private companyApi: CompanyApiService,
    private applicationApi: ApplicationsApiService,
    private personalInfoService: PersonalInfoService,
    private contactsService: ContactsService,
    private activatedRoute: ActivatedRoute,
    private trackingService: TrackingService
  ) {}

  public set agreedToTerms(agreedToTerms: boolean) {
    this._agreedToTerms.next(agreedToTerms);
  }

  public get agreedToTerms(): boolean {
    return this._agreedToTerms.getValue();
  }

  public set amount(amount: number) {
    this._amount.next(amount);
  }

  public get amount(): number {
    return this._amount.getValue();
  }

  public set personNumber(personNumber: string) {
    this._personNumber.next(personNumber);
  }

  public get personNumber(): string {
    return this._personNumber.getValue();
  }

  public set company(company: CompanyLookupResponseModel) {
    this._company.next(company);
  }

  public get company(): CompanyLookupResponseModel {
    return this._company.getValue();
  }

  public set duration(duration: number) {
    this._duration.next(duration);
  }

  public get duration(): number {
    return this._duration.getValue();
  }

  public set email(email: string) {
    this._email.next(email);
  }

  public get email(): string {
    return this._email.getValue();
  }

  public set companyNameOrId(companyNameOrId: string) {
    this._companyNameOrId.next(companyNameOrId);
  }

  public get companyNameOrId(): string {
    return this._companyNameOrId.getValue();
  }

  public set reason(reason: string) {
    this._reason.next(reason);
  }

  public get reason(): string {
    return this._reason.getValue();
  }

  public set phoneNumber(phoneNumber: string) {
    this._phoneNumber.next(phoneNumber);
  }

  public get phoneNumber(): string {
    return this._phoneNumber.getValue();
  }

  public set leadId(leadId: string) {
    this._leadId.next(leadId);
  }

  public get leadId(): string {
    return this._leadId.getValue();
  }

  public set companyAssociation(companyAssociation: string) {
    this._companyAssociation.next(companyAssociation);
  }

  public get companyAssociation(): string {
    return this._companyAssociation.getValue();
  }

  public set ticketType(ticketType: any) {
    this._ticketType.next(ticketType);
  }

  public get ticketType(): any {
    return this._ticketType.getValue();
  }

  public handlePostLogin(
    firstname?: string,
    lastname?: string,
    middlename?: string,
    gender?: string,
    dateOfBirth?: string,
    estimatedTurnover?: number,
    estimatedTurnoverPrev?: number,
    estimatedMonthlySales?: number
  ): Observable<AddApplicationResponseModel> {
    const addContactBody: AddContactRequestModel = {
      personalIdNumber: this.personNumber,
      email: this.email,
      phoneNumber: this.phoneNumber.toString(),
      firstname,
      lastname,
      middlename,
      companyName: this.company.companyName,
      leadId: this.leadId,
      gender,
      dateOfBirth,
      agreedToTerms: this.agreedToTerms,
    };

    const addCompanyBody: AddCompanyRequestModel = {
      orgNumber: this.company.orgNumber,
      companyName: this.company.companyName,
      companyType: this.company.companyType,
      registrationDate: this.company.registrationDate,
      isActive: this.company.isActive,
    };

    // Populate add application request with tracking data
    const addApplicationBody = this.trackingService.populateTrackingData({
      agreedToTerms: this.agreedToTerms,
      amount: this.amount,
      companyAssociation: this.companyAssociation,
      companyId: 0, // It will be populated after request to create company
      contactId: 0, // It will be populated after request to create contact
      duration: this.duration,
      reason: this.reason,
      ticketType: this.ticketType,
      estimatedTurnover,
      estimatedTurnoverPrev,
      estimatedMonthlySales,
    }) as AddApplicationRequestModel;

    return zip(
      this.contactsApi.addContact(addContactBody),
      this.companyApi.addCompany(addCompanyBody)
    ).pipe(
      tap((responses: [AddContactResponseModel, AddCompanyResponseModel]) => {
        this.personalInfoService.firstName = responses[0].firstname;
        this.personalInfoService.lastname = responses[0].lastname;
        this.contactsService.contactId = responses[0].id;
        addApplicationBody.contactId = responses[0].id;
        addApplicationBody.companyId = responses[1].id;
      }),
      switchMap(() => this.applicationApi.addApplication(addApplicationBody))
    );
  }

  public populateServiceWithGetApplicationResponseData(
    data: GetApplicationResponseModel
  ): void {
    this.amount = data.amount;
    this.duration = data.duration;
    this.email = (data?.contacts && data?.contacts[0]?.email) || '';
    this.phoneNumber =
      formatPhoneNumberBackToClient(
        data?.contacts && data?.contacts[0]?.phoneNumber
      ) || '';
    this.companyApi
      .companyLookup(data.company.orgNumber)
      .subscribe((response) => {
        this.company = {
          orgNumber: response.orgNumber,
          companyType: response.companyType,
          companyName: response.companyName,
          registrationDate: response.registrationDate,
          isActive: response.isActive,
        };
      });
    this.ticketType = data.ticketType;
  }

  public populateServiceWithGetLeadResponseData(
    data: GetLeadResponseModel
  ): void {
    this.leadId = data.id!;
    this.agreedToTerms = true;
    this.amount = data.amount!;
    this.duration = data.duration!;
    this.phoneNumber = data.phoneNumber;
    this.email = data.email;
    this.reason = data.reason!;
  }

  canActivate(): boolean {
    if (this.ticketType === TicketType.BIG) {
      return true;
    }
    if (
      this.amount === 0 ||
      this.duration === 0 ||
      this.email === '' ||
      this.phoneNumber === '' ||
      this.reason === '' ||
      !this.company ||
      this.leadId === ''
    ) {
      this.router.navigate(['basic-info']);
      return false;
    }
    return true;
  }

  public fetchQueryParams(): void {
    const amountParam =
      this.activatedRoute.snapshot.queryParamMap.get('amount');
    const durationParam =
      this.activatedRoute.snapshot.queryParamMap.get('duration');
    const maxAmountParam =
      this.activatedRoute.snapshot.queryParamMap.get('max_amount');
    const agreedToTermsParam =
      this.activatedRoute.snapshot.queryParamMap.get('agreedToTerms');

    if (amountParam) {
      this.amount = Number(amountParam);
    } else if (maxAmountParam) {
      this.amount = Math.round(Number(maxAmountParam) / 500) * 500 * 0.5;
    }

    if (durationParam) {
      this.duration = Number(durationParam);
    }

    if (agreedToTermsParam === 'true') {
      this.agreedToTerms = Boolean(agreedToTermsParam);
    }
  }
}
